import React from 'react';

import { UserDataGroupResponse } from '@ariksa/abac/api';
import { Box, HStack, Stack } from '@chakra-ui/react';
import { useSelector } from 'react-redux';

import { ButtonGroup, CancelButton, Form } from 'components/DataEntry';
import {
  CustomRoleIcon,
  ExclamationCircleIcon,
  RoleIcon,
  UserIcon,
} from 'components/Icons';
import {
  inlineObjectFieldStyles,
  PrimaryButton,
  StackedHeader,
} from 'components/index';
import { Modal } from 'components/Overlay';
import { JITInformationAlert } from 'containers/Jit/Setup/Components/Utils/JITInformationAlert';

import { selectDataCatalog } from '../selectors';

import { renderPermissions } from './utils';

export interface PermissionOption {
  value: string;
  // The name over its description, as shown in the dropdown.
  label: React.ReactNode;
  // Name shown on the chip, where the group heading is not visible.
  chip: string;
}

export interface RoleOption {
  label: string;
  value: string;
  crudLevels?: string[];
}

export const accessTypeOptions = {
  role: { label: 'Pre-defined Role', value: 'role', icon: <RoleIcon /> },
  custom: { label: 'Custom Role', value: 'custom', icon: <CustomRoleIcon /> },
};

// ButtonGroup shows an item's icon alone, so the icon goes in the label.
const accessTypeTab = ({
  label,
  icon,
}: {
  label: string;
  icon: React.ReactNode;
}) => (
  <HStack spacing={2}>
    <Box boxSize={4}>{icon}</Box>
    <Box>{label}</Box>
  </HStack>
);

export interface RequestAccessFormState {
  roleOptions: RoleOption[];
  role?: RoleOption;
  accessType?: { label: string; value: string; icon?: React.ReactNode };
  permissions: PermissionOption[];
  duration?: any;
  time?: { startDate?: Date; startTime?: string };
  reason?: string;
  isSubmitDisabled?: boolean;
}

export interface RequestAccessFormProps {
  isOpen: boolean;
  onClose: () => void;
  dataGroup: UserDataGroupResponse;
  handleSubmit: () => void;
  state: RequestAccessFormState;
  updateState: (state: Partial<RequestAccessFormState>) => void;
  isReadOnly?: boolean;
  permissionGroups: Array<{ label: string; options: PermissionOption[] }>;
  customUnavailableReason?: string | null;
  isCustomAvailable: boolean;
}

export const RequestAccessForm = ({
  isOpen,
  onClose,
  dataGroup,
  handleSubmit,
  state,
  updateState,
  isReadOnly = false,
  permissionGroups,
  customUnavailableReason,
  isCustomAvailable,
}: RequestAccessFormProps) => {
  const { requestAction } = useSelector(selectDataCatalog);
  const isCustom = state.accessType?.value === 'custom';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
      header={
        <StackedHeader
          icon={<UserIcon />}
          upper={isReadOnly ? 'View Request' : 'Request JIT Access'}
          lower={dataGroup.name}
        />
      }
      body={
        <Stack spacing={4}>
          {!isReadOnly && (
            <JITInformationAlert
              icon={<ExclamationCircleIcon color="primary" />}
              description="You will receive a notification when your request has been approved"
            />
          )}
          <Form
            key={state.roleOptions.map(option => option.value).join('|')}
            formOptions={{
              defaultValues: {
                accessType: accessTypeOptions.role,
                duration: { label: '4 h', value: '4h' },
                role: state.role,
                time: state.time,
              },
            }}
            schema={{
              accessType: {
                type: 'custom-with-form-control',
                label: 'Access Type',
                value: state.accessType ?? accessTypeOptions.role,
                component: (
                  <ButtonGroup
                    items={[
                      {
                        value: accessTypeOptions.role.value,
                        label: accessTypeTab(accessTypeOptions.role),
                        isDisabled: isReadOnly,
                      },
                      {
                        value: accessTypeOptions.custom.value,
                        label: accessTypeTab(accessTypeOptions.custom),
                        isDisabled: isReadOnly || !isCustomAvailable,
                      },
                    ]}
                    value={state.accessType ?? accessTypeOptions.role}
                    onClick={item =>
                      updateState({ accessType: accessTypeOptions[item.value] })
                    }
                    // Two halves span the full field width.
                    styles={{ button: { w: '50%' } }}
                  />
                ),
                tooltip: customUnavailableReason ?? undefined,
              },
              ...(isCustom
                ? {
                    permissions: {
                      type: 'react-select',
                      label: 'Select Permissions',
                      options: permissionGroups,
                      placeholder: 'Select permissions',
                      isMulti: true,
                      isSearchable: false,
                      closeMenuOnSelect: false,
                      hideSelectedOptions: false,
                      showTotalSelected: true,
                      selectedMessage: 'Permissions',
                      isRequired: true,
                      isDisabled: isReadOnly,
                      value: state.permissions,
                      helperText: renderPermissions({
                        labels: state.permissions.map(option => option.chip),
                      }),
                      onChange: value =>
                        updateState({ permissions: value ?? [] }),
                    },
                  }
                : {
                    role: {
                      type: 'react-select',
                      label: 'Select Role',
                      options: state.roleOptions,
                      placeholder: 'Select a role',
                      isRequired: true,
                      isDisabled: isReadOnly,
                      value: state.role,
                      helperText: renderPermissions({
                        labels: state.role?.crudLevels ?? [],
                      }),
                      onChange: value => updateState({ role: value }),
                    },
                  }),
              duration: {
                type: 'react-select',
                label: 'Duration',
                value: state.duration ?? { label: '4 h', value: '4h' },
                options: [
                  { label: '1 h', value: '1h' },
                  { label: '4 h', value: '4h' },
                  { label: '8 h', value: '8h' },
                  { label: '12 h', value: '12h' },
                ],
                isDisabled: isReadOnly,
                onChange: value => updateState({ duration: value }),
              },
              time: {
                type: 'object',
                properties: {
                  startDate: {
                    type: 'dayPicker',
                    label: 'Start Date',
                    // A closed picker would otherwise extend the modal's scroll.
                    isLazy: true,
                    isDisabled: isReadOnly,
                    dateProps: {
                      fromDate: new Date(),
                      mode: 'single',
                      selected: state.time?.startDate,
                      onSelect: value =>
                        updateState({
                          time: { ...state.time, startDate: value },
                        }),
                    },
                  },
                  startTime: {
                    type: 'timePicker',
                    label: 'Start Time',
                    isLazy: true,
                    isDisabled: isReadOnly,
                    value: state.time?.startTime,
                    onChange: value =>
                      updateState({
                        time: { ...state.time, startTime: value },
                      }),
                  },
                },
                styles: {
                  objectContainer: {
                    align: 'start',
                    isInline: true,
                    spacing: 4,
                  },
                },
              },
              reason: {
                type: 'text',
                label: 'Reason',
                placeholder: 'This is why I need access',
                isRequired: true,
                isDisabled: isReadOnly,
                value: state.reason,
                onChange: value => updateState({ reason: value }),
              },
            }}
            styles={{
              objectField: {
                ...inlineObjectFieldStyles,
                propertyContainer: { w: '50%' },
              },
            }}
            handleSubmit={handleSubmit}
            buttonOptions={{
              submit: { name: 'OK', isVisible: false, isDisabled: isReadOnly },
            }}
          />
        </Stack>
      }
      footer={
        isReadOnly ? (
          <PrimaryButton px={4} onClick={onClose}>
            OK
          </PrimaryButton>
        ) : (
          <HStack justify="space-between" w="full">
            <CancelButton onClick={onClose}>CANCEL</CancelButton>
            <PrimaryButton
              type="submit"
              px={6}
              isLoading={requestAction.isLoading}
              isDisabled={
                state.isSubmitDisabled ||
                (isCustom && (!isCustomAvailable || !state.permissions.length))
              }
              onClick={() => handleSubmit()}
            >
              OK
            </PrimaryButton>
          </HStack>
        )
      }
    />
  );
};
